import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import {
  CHANGE_DETECTION_FEATURE_CODE,
  DOC_GENERATION_FEATURE_CODE,
  KNOWLEDGE_ALERTS_FEATURE_CODE,
  KNOWLEDGE_HUB_FEATURE_CODE,
} from '../../features/plans/models/entitlement.model';
import { PlanFeatureService } from '../../features/plans/services/plan-feature.service';
import { LanguageService } from '../../services/language/language.service';

/** Definición estática de una pestaña del Knowledge Hub (una por cada feature reencuadrada). */
interface KnowledgeHubTab {
  /** Segmento hijo relativo al padre `/knowledge-hub` (coincide con el path de la ruta lazy). */
  readonly path: string;
  /** Icono Material mostrado en la barra de pestañas. */
  readonly icon: string;
  /** Clave i18n del título de la pestaña (reusa las etiquetas del sidebar). */
  readonly labelKey: string;
  /** Código de feature del plan que habilita la pestaña. */
  readonly featureCode: string;
}

/**
 * Shell con barra de pestañas deep-linkable que agrupa las 4 secciones de conocimiento
 * (Documentos Generados, Conocimiento, Cambios, Alertas) bajo `/knowledge-hub`.
 *
 * Cada pestaña es una ruta hija lazy que conserva su feature guard; la barra solo muestra
 * las pestañas cuya feature está habilitada en el plan efectivo. Sin hija activa, redirige
 * a la primera pestaña habilitada (o a `/home` si ninguna lo está).
 */
@Component({
  selector: 'app-knowledge-hub',
  standalone: true,
  imports: [CommonModule, RouterModule, MatTabsModule, MatIconModule],
  templateUrl: './knowledge-hub.component.html',
  styleUrls: ['./knowledge-hub.component.scss'],
})
export class KnowledgeHubComponent implements OnInit, OnDestroy {
  private static readonly BASE_PATH = '/knowledge-hub';

  /** Orden canónico (idéntico al que tenía el sidebar). */
  private readonly allTabs: readonly KnowledgeHubTab[] = [
    { path: 'generate', icon: 'description', labelKey: 'nav.generate', featureCode: DOC_GENERATION_FEATURE_CODE },
    { path: 'knowledge', icon: 'hub', labelKey: 'nav.knowledge', featureCode: KNOWLEDGE_HUB_FEATURE_CODE },
    { path: 'changes', icon: 'compare_arrows', labelKey: 'nav.changes', featureCode: CHANGE_DETECTION_FEATURE_CODE },
    { path: 'alerts', icon: 'notifications', labelKey: 'nav.alerts', featureCode: KNOWLEDGE_ALERTS_FEATURE_CODE },
  ];

  /** Pestañas visibles según los flags resueltos del plan. */
  visibleTabs: readonly KnowledgeHubTab[] = [];

  private flagsResolved = false;
  private readonly subscriptions = new Subscription();

  constructor(
    public languageService: LanguageService,
    private readonly planFeatures: PlanFeatureService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    // Resuelve los 4 flags igual que el sidebar (degrada a oculto si falla la consulta).
    const flagStreams = this.allTabs.map((tab) => this.planFeatures.hasFeature(tab.featureCode).pipe(take(1)));
    this.subscriptions.add(
      combineLatest(flagStreams).subscribe((flags) => {
        this.visibleTabs = this.allTabs.filter((_, index) => flags[index]);
        this.flagsResolved = true;
        this.redirectToDefaultTabIfNeeded();
      })
    );

    // Reevalúa el default cuando se vuelve al padre "pelado" (p. ej. clic en el sidebar).
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => this.redirectToDefaultTabIfNeeded())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  translate(key: string): string {
    return this.languageService.translate(key);
  }

  /**
   * Si estamos exactamente en `/knowledge-hub` (sin pestaña hija) y ya se resolvieron los
   * flags, navega a la primera pestaña habilitada; si ninguna lo está, vuelve a `/home`.
   */
  private redirectToDefaultTabIfNeeded(): void {
    if (!this.flagsResolved) {
      return;
    }
    const url = this.router.url.split('?')[0].split('#')[0];
    if (url !== KnowledgeHubComponent.BASE_PATH) {
      return;
    }
    if (this.visibleTabs.length === 0) {
      void this.router.navigate(['/home']);
      return;
    }
    void this.router.navigate([KnowledgeHubComponent.BASE_PATH, this.visibleTabs[0].path]);
  }
}
